import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  addTagSvg,
  deleteTagSvg,
  editSvg,
  settingSvg,
  trashSvg,
} from "../assets/icons";
import { showConfirmToast } from "../components/ConfirmToast";
import { PaymentModal } from "../components/PaymentModal";
import { deleteTicket, getTicketsByUser } from "../data/httpClient";
import { useTournamentStateContext } from "../data/providers/TournamentStateProvider";
import { Ticket } from "../types";
import { EuserRole } from "../types/euserRole";
import { makeAccessibleClick } from "../utils/makeAccessibleClick";
import { notify } from "../utils/notify";
import useEuser from "../utils/useEuser"; // access to provider via "use" hook

export const AccountReservations = () => {
  const navigate = useNavigate();
  const { getMostRecentLiveTournament } = useTournamentStateContext();
  const { euser } = useEuser();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isSponsorTicketCategory, setIsSponsorTicketCategory] = useState<
    boolean | null
  >(null);
  const [tournamentRegisterUrl, setTournamentRegisterUrl] = useState("/");
  const [upcomingTournamentId, setUpcomingTournamentId] = useState<number>();
  const [isDataLoading, setIsDataLoading] = useState(false);

  const [filterTags, setFilterTags] = useState([
    {
      name: "Upcoming",
      selected: true,
    },
    {
      name: "Past",
      selected: false,
    },
  ]);

  // ToDo: assumes that there is no more than one upcoming tournament (doesn't use any start dates)
  const getTicketsResult = async (upcomingTournId: number | undefined) => {
    if (euser) {
      let result = await getTicketsByUser(parseInt(euser.id));

      setTickets(result);

      // BASIC FILTERS
      filterTags
        .filter((f) => f.selected)
        .forEach((f) => {
          result = result.filter((t: Ticket) => {
            if (f.name === "Upcoming") {
              return t.tournamentId === upcomingTournId;
            } else {
              return t.tournamentId !== upcomingTournId;
            }
          });
        });
      setTickets(result);
      // END BASIC FILTERS
    }
  };

  const getTournamentRegisterUrl = (tournamentId: number | undefined) => {
    const url = tournamentId ? `/tournaments/${tournamentId}/register` : "/";
    setTournamentRegisterUrl(url);
  };

  const userCanAdministerSponsor = (sponsorId: number): boolean => {
    return (
      euser?.roles.includes(EuserRole.Admin) ||
      (euser?.managedSponsorIds ?? []).includes(sponsorId)
    );
  };

  const numberToCurrency = (num: number, digits: number) => {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: digits,
    });
    return formatter.format(num);
  };

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        setIsDataLoading(true);
        const tournament = await getMostRecentLiveTournament();
        // It'd be better to check "active" (to prevent race conditions) after each data fetching but before each state setting vs before an encapsulating function.
        if (active) {
          await getTicketsResult(tournament?.id);
        }
        if (active) {
          getTournamentRegisterUrl(tournament?.id);
          setUpcomingTournamentId(tournament?.id);
        }
      } catch (error) {
        // request errors (i.e. axios errors) already print to the console and alert user of error (see httpClient.ts)
        if (!axios.isAxiosError(error)) {
          console.error("Error fetching data:", error);
          notify.error("There was an error fetching data.");
        }
      } finally {
        if (active) {
          setIsDataLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [euser, filterTags]);

  return (
    <div className="editPageWrapper accountReservationsPage">
      <h3 className="list_title">Tickets and Reservations</h3>
      <p className="reservationIntro">
        Here you can manage the tickets you&apos;ve reserved. These could be
        tickets you plan to attend with yourself or tickets you plan to invite
        other attendees to use. In order to invite other attendees and manage
        groups, click the value in the &quot;Groups&quot; column. If you want to
        view tickets assigned to you as an attendee or view your scannable QR
        codes, head to the &quot;Entrance Passes&quot; page. Need help managing
        your tickets? Email{" "}
        <a href="mailto:golfforhope@hcahealthcare.com?subject=Need%20help%20managing%20my%20tickets">
          golfforhope@hcahealthcare.com
        </a>
        .
      </p>

      {/* BASIC FILTERS */}
      <article className="filterWrapper">
        {filterTags.map((f, i) => {
          return (
            <button
              key={`ticket-filterTag-${i}`}
              className={`filterTag ${f.selected && "tagSelected"}`}
              onClick={() => {
                filterTags[i].selected = !filterTags[i].selected;
                // deselect other tags if one is selected
                if (filterTags[i].selected === true) {
                  filterTags.forEach((x) => {
                    if (x.name !== filterTags[i].name) {
                      x.selected = false;
                    }
                  });
                }
                setFilterTags([...filterTags]);
              }}
            >
              <span>{f.name}</span>
              {f.selected ? (
                <img src={deleteTagSvg} alt="unselected filter tag" />
              ) : (
                <img src={addTagSvg} alt="selected filter tag" />
              )}
            </button>
          );
        })}
      </article>
      {/* END BASIC FILTERS */}

      <table className="ticketsTable reservationsTable">
        <thead>
          <tr>
            <th>Event</th>
            <th>Ticket Category</th>
            <th>Sponsor</th>
            <th>Groups</th>
            <th>Price</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {isDataLoading && (
            <tr>
              <td>Loading...</td>
            </tr>
          )}

          {!isDataLoading && !tickets.length && (
            <tr>
              {!filterTags.find(
                (tag) => tag.name === "Past" && tag.selected === true
              ) ? (
                <td>
                  <a
                    href={tournamentRegisterUrl}
                    title={"register for an event"}
                  >
                    Register
                  </a>{" "}
                  to get tickets to an event!
                </td>
              ) : (
                <td>No tickets found.</td>
              )}
            </tr>
          )}

          {!isDataLoading && tickets.length
            ? tickets.map((t: Ticket, i: number) => {
                return (
                  <tr key={i}>
                    <td>{t.tournament?.name}</td>

                    <td>{t.ticketCategory?.name}</td>

                    {t.sponsor ? (
                      <>
                        {userCanAdministerSponsor(t.sponsorId) ? (
                          <td className="tableLink">
                            {t.sponsor && (
                              <span
                                {...makeAccessibleClick(() =>
                                  navigate(
                                    `/account/sponsor/${t.sponsorId}/edit`
                                  )
                                )}
                              >
                                <img
                                  src={editSvg}
                                  alt="manage sponsor"
                                  className="small"
                                />
                                &nbsp;
                                {t.sponsor?.name}
                              </span>
                            )}
                          </td>
                        ) : (
                          <td>{t.sponsor?.name}</td>
                        )}
                      </>
                    ) : (
                      <td></td>
                    )}

                    {!isDataLoading &&
                    t.tournamentId === upcomingTournamentId ? (
                      <td className="tableLink">
                        <span
                          {...makeAccessibleClick(() => {
                            navigate(`/account/tickets/${t.id}/groups`);
                          })}
                        >
                          <img
                            src={settingSvg}
                            className="iconAction"
                            alt="manage ticket groups"
                          />
                          &nbsp;
                          {t.groups.length}&nbsp;Group
                          {t.groups.length > 1 ? "s" : null}
                        </span>
                      </td>
                    ) : (
                      <td>
                        <span>
                          {t.groups.length}&nbsp;Group
                          {t.groups.length > 1 ? "s" : null}
                        </span>
                      </td>
                    )}

                    <td>{numberToCurrency(t.ticketCategory.price, 0)}</td>

                    <td>
                      {t.paid ? (
                        <div className="reservationPaidStatus">
                          Paid:{" "}
                          <span style={{ whiteSpace: "nowrap" }}>
                            {t.datePaid}
                          </span>
                        </div>
                      ) : (
                        t.tournamentId === upcomingTournamentId && (
                          // show button
                          <button
                            className="primaryBtn"
                            type="button"
                            onClick={() => {
                              setIsSponsorTicketCategory(!!t.sponsorId);
                              setShowPaymentModal(true);
                            }}
                          >
                            Begin Payment
                          </button>
                        )
                      )}
                    </td>

                    {/* admin users can also cancel vs delete a ticket; see TicketsPage.tsx */}
                    {!t.paid && t.tournamentId === upcomingTournamentId ? (
                      <td>
                        <button
                          type="button"
                          className="iconBtn"
                          onClick={async () => {
                            showConfirmToast({
                              title: "Confirm Deletion",
                              message:
                                "Are you sure you want to delete this ticket?",
                              btnText: "Delete",
                              onConfirm: async () => {
                                await deleteTicket(t.id);
                                notify.success("Ticket deleted successfully.");
                                const tournament =
                                  await getMostRecentLiveTournament();
                                getTicketsResult(tournament?.id);
                              },
                            });
                          }}
                        >
                          <img
                            src={trashSvg}
                            alt="Delete ticket"
                            className="iconAction"
                          />
                        </button>
                      </td>
                    ) : (
                      <td></td>
                    )}
                  </tr>
                );
              })
            : ""}
        </tbody>
      </table>

      <section>
        <PaymentModal
          isOpen={showPaymentModal}
          onRequestClose={() => {
            setIsSponsorTicketCategory(null);
            setShowPaymentModal(false);
          }}
          isSponsorTicketCategory={isSponsorTicketCategory}
        />
      </section>
    </div>
  );
};
